import {Button, Icon, Popup} from '@startlibs/components';
import {darken} from 'polished';
import {getColor, media, oneWayAnimation} from '@startlibs/utils';
import {usePopupToggle, useToggle, WithLazyResource} from '@startlibs/core';
import React from 'react';
import styled, {createGlobalStyle, css} from 'styled-components';
import { InfoBox } from './InfoBox';
import {lazyProviderInfo} from './WithProvider'
import {FormattedMessage} from "react-intl";

export const CMSBackground = createGlobalStyle`
  body {
    background-color: #F3F4F8 !important;
  }
`

export const PageContainer = styled.div`
  padding: 8rem 1.5rem 160px;
  margin: 0 auto;
  position: relative;
  min-width: 300px;
  max-width: 66rem;
  min-height: 100vh;
  ${props => props.inCMS && css`
    padding-top: 2rem;
    ${media.max(520)`
      padding-top: 2rem;
    `}
  `}
  ${props => props.fullWidth && `
    max-width: 100%;
  `}
  ${props => props.appLayout && `
    padding-top: 2rem;
    padding-bottom: 2rem;
  `}
  ${media.max(739)`
    padding-left: 1rem;
    padding-right: 1rem;
  `}
  ${media.max(520)`
    padding-top: ${props => props.appLayout ? '1rem' : '6.25rem'};
  `}
  ${media.mobile`
    padding-bottom: 7rem;
    ${props => props.theme.mobileSidebar &&`
      padding-top: 12rem;
    `}
  `}
  h3 {
    font-weight: 600;
  }
  p {
    font-size: 13px;
  }
  ${props => props.noMarginOnPrint ? css`
    @media print {
      padding: 0 !important;
    }
  `:`
    @media print {
      padding: 20mm;
    }
  `}
  ${props => props.withPrint && css`
    @media print {
      padding: 0mm 5mm 0mm 5mm !important;
    }
  `}
`

export const SectionHeading = styled.div `
  margin-bottom: 1rem;
  h3 {
    margin-bottom: 0.5rem;
  }
  sup {
    color: ${getColor('alert')};
  }
`

export const Card = styled.div`
  padding: 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  margin: 0 auto;
  margin-bottom: 2rem;
  & ~ ${SectionHeading} {
    margin-top: 3rem;
  }
  ${media.max(520)`
    padding: 1.5rem;
  `}
  ${props => props.noPrint && css`
    @media print {
      display: none;
    }
  `}
`

export const CardWithIcon = styled(Card)`
  padding-right: 13rem;
  position: relative;
  ${media.max(520)`
    padding-right: 13rem;
  `}
`

export const CardWithIconMinHeigth = styled(Card)`
  padding-right: 13rem;
  min-height: 156px;
  position: relative;
  ${media.max(520)`
    padding-right: 13rem;
  `}
`

export const RequestStatusIcon = styled(Icon)`
  display: inline-block;
  font-size: 50px;
  border-radius: 50%;
  text-align: center;
  height: 74px;
  line-height: 67px;
  width: 74px;
  border: 3px solid ${getColor('gray150')};
  color: ${getColor('gray150')};
  right: 3.5rem;
  top: 3rem;
  position: absolute;
  ${props => props.success && css`
    border-color: ${getColor('success')};
    color: ${getColor('success')};
  `}
`

export const PageFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid ${getColor('gray210')};
  ${props => props.NoHR && `border-top: 0;`}
  padding-top: 1.25rem;
  margin-top: 1rem;
  .right-wrapper {
    padding-left: 1rem;
    margin-left: auto;
    text-align: right;
  }
  ${Button} {
      margin-bottom: 0.75rem;
      margin-top: 0.75rem;
    }
  ${media.max(468)`
    flex-wrap: wrap;
    flex-direction: column-reverse;
    ${Button} {
      flex-basis: 100%;
      margin-left: 0;
      margin-right: 0;
      margin-bottom: 0;
    }
    .right-wrapper {
      display: flex;
      flex-wrap: wrap;
      flex-basis: 100%;
      margin-left: 0;
      padding-left: 0;
      margin-bottom: 0.5rem;
      margin-top: -0.5rem;
      flex-direction: column-reverse;
      ${Button} {
        margin-left: 0;
        margin-top: 0.5rem;
      }
    }
    &.reviewed-footer {
      .right-wrapper {
        flex-wrap: nowrap;
        flex-basis: 100%;
        margin-left: 0;
        flex-direction: row;
        ${Button} {
          flex-grow: 1;
          margin-top: 0;
          display: inline-flex;
          padding: 0 1.25rem;
          white-space: nowrap;
          &.pdf-btn {
            min-width: 105px;
          }
          & ~ ${Button} {
            margin-left: 1rem;
          }
        }
      }
    }
  `}
  /* IE11 Adjustments */
  @media (max-width: 486px) and (-ms-high-contrast:none) {

     & { flex-direction: row; } /* IE10 */
     *::-ms-backdrop, &.reviewed-footer { flex-direction: row; } /* IE11 */

     & .right-wrapper { margin-top: .5rem; } /* IE10 */
     *::-ms-backdrop, .right-wrapper { margin-top: .5rem; } /* IE11 */

     & .right-wrapper ${Button} ~ ${Button} { margin-left: .5rem; } /* IE10 */
     *::-ms-backdrop, & & .right-wrapper ${Button} ~ ${Button} { margin-left: .5rem; } /* IE11 */

   }
`

export const HeaderButtons = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1.25rem 0;
  .right-wrapper {
    margin-left: 1rem;
  }
  ${media.max(468)`
    flex-wrap: wrap;
    flex-direction: column-reverse;
    .check-request, .exit-shared {
      display: none;
    }
    .right-wrapper {
      display: flex;
      flex-wrap: wrap;
      flex-basis: 100%;
      margin-left: 0;
      flex-direction: row;
      width: 100%;
      ${Button} {
        flex-grow: 1;
      }
    }
  `}
  ${props => props.noPrint && css`
    @media print {
      display: none;
    }
  `}
  /* IE11 Adjustments */
  @media (max-width: 486px) and (-ms-high-contrast:none) {

     & { flex-direction: row; } /* IE10 */
     *::-ms-backdrop, & { flex-direction: row; } /* IE11 */

   }
`

const fadeInUp = oneWayAnimation('transform: translateY(10px); pointer-events: none; opacity: 0;', 'transform: translateY(0); pointer-events: none; opacity: 1;')

export const HelpButton = styled(({className}) => {
    const helpBubble = usePopupToggle()

    return <div className={className} onClick={helpBubble.open}>
        <Icon icon="help"/>
        {
            helpBubble.isOpen &&
            <HelpBubble/>
        }
    </div>
})`
  position: fixed;
  z-index: 10;
  bottom: 1.5rem;
  right: 1.75rem;
  width: 50px;
  text-align: center;
  border-radius: 5rem;
  background: ${getColor('main')};
  cursor: pointer;
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.25);
  :hover {
    background: ${props => darken(0.035, getColor('main')(props))};
  }
  :active {
    background: ${props => darken(0.06, getColor('main')(props))};
  }
  ${Icon} {
    font-size: 38px;
    color: white;
    line-height: 50px;
  }
  @media print {
    display: none;
  }
`

export const HelpBubble = styled(({className}) =>
  <WithLazyResource wrap fallback={null} value={lazyProviderInfo}>{providerInfo =>
    <Popup
      arrow="-28" offset="0 15" className={className}
      animation={fadeInUp}
    >
      <h4><FormattedMessage
        defaultMessage="Need help?"
        description="Help bubble title"
      /></h4>
      {
        providerInfo?.needHelp ? <p dangerouslySetInnerHTML={{__html:providerInfo.needHelp}}/> :
        <p>
          <FormattedMessage
            defaultMessage={`Contact <email>{supportEmail}</email> or
          <nowrap> call <b>{phone}</b>.</nowrap>`}
            description="Help bubble message"
            values={{
              email: (chunks) => <a className="link" href={"mailto:" + chunks[0]} target="_blank">{chunks}</a>,
              supportEmail: 'support@purview.net',
              phone: "+1 800-501-1537",
              nowrap: (chunks) => <span className="nowrap">{chunks}</span>,
              b: (chunks) => <b>{chunks}</b>
            }}
          />
        </p>
      }
    </Popup>
  }</WithLazyResource>)`
  z-index: 10;
  padding: 1.5rem;
  border-radius: 10px;
  background: white;
  max-width: 22rem;
  color: rgba(0,0,0,0.5);
  h4 {
    border-bottom: 1px solid rgba(0,0,0,0.15);
    padding-bottom: 1rem;
  }
  p {
    font-size: 14px;
  }
  .link {
    color: ${getColor('main')};
    font-weight: 600;
  }
  ${Button} {
    width: 100%;
  }
  @media print {
    display: none;
  }
`

export const ResponsiveNotice = styled(({className}) => {

  const notice = useToggle(true)

  if (!notice.isOpen) {
    return null
  }

  return <InfoBox lightYellow withIcon className={className}>
      <Icon icon="warning" /> <div><strong>Smaller screens not supported. </strong> We recommend using a screen at least 1024px wide.</div>
      <Icon className="dismiss" icon="x" onClick={notice.close} />
  </InfoBox>
})`
  @media print {
    display: none !important;
  }
  ${media.min(1024)`
    display: none;
  `}
  width: auto;
  max-width: calc(100% - 8rem);
  position: fixed;
  bottom: .75rem;
  right: 6.5rem;
  z-index: 10;
  .dismiss {
    margin-left: 1rem;
    cursor: pointer;
  }
`
